// Cockpit tool catalog — single source of truth.
//
// Both the UI (`ui/src/data/tools.config.ts`) and the pins Lambda
// (`packages/core-lambdas/me-pins-catalog.ts`) re-export from this file.
// Adding, removing, or renaming a tool happens here, in one place.
//
// The server-side allowlist (VALID_TOOL_SLUGS) is derived from
// COCKPIT_DEPARTMENTS at module load, so it cannot drift from the UI.

export type ToolStatus = 'live' | 'progress' | 'planned';

export type Tool = {
  id: string;
  name: string;
  desc: string;
  status: ToolStatus;
  owner: string;
  // false = hidden from sidebar and home page. Absent or true = visible.
  // Routes are NOT removed so devs can still reach unlaunched tools by URL.
  launched?: boolean;
  // Optional metadata carried from the handoff. Not used by the shell yet.
  channel?: string;
  current?: boolean;
  // Binary access-grant slug (spec 00_access-binary-grants.md), namespaced
  // `section.tool` (e.g. `commerce.orders`, `stores.sweetspot`). A user is
  // permitted to use this tool when their permissions include this exact
  // string, or when they are a super-admin. Present on every grantable tool;
  // absent on Everyone tools (see `capability` below) and on unlaunched
  // placeholders that have no backend yet. One grant row = one person + one
  // accessSlug; several catalog tools MAY share the same accessSlug when they
  // are really one grantable unit (the store-facing app: Today, Store thread,
  // Inventory Count, and the Sweet Spot console row all share
  // `stores.sweetspot`). Read by `ui/src/lib/toolVisibility.ts` and by
  // `ui/src/lib/accessScopes.ts` (which builds the Administration > Access
  // console straight off this field; there is no separate scope-mapping table
  // anymore).
  accessSlug?: string;
  // Optional capability string. Used ONLY for the small fixed set of
  // "Everyone" tools (Chat, HR Directory, Fleet Map): access is automatic for
  // every signed-in employee (the `core:read` session baseline) rather than an
  // explicit grant, so these tools carry `capability` instead of `accessSlug`.
  // When set, the sidebar hides this tool from any user whose permissions do
  // not include it (exact match). Absent AND no accessSlug = visible to all.
  capability?: string;
  // Optional Confluence KB page ID. When set, the Help panel opens directly
  // to this article when the user is on this tool. Absent = show index.
  helpKbPageId?: string;
  // Optional sidebar subgroup label. Only the Stores department uses this
  // today (Option A redesign, 2026-08-08): 'Daily ops' / 'Performance' /
  // 'Inventory' / 'Setup'. A tool with no group renders as a plain nested
  // row, unlabeled, same as before this field existed. Purely a UI grouping
  // hint; it carries no access-control meaning and does not affect
  // VALID_TOOL_SLUGS, accessSlug, or the Access-console parity test.
  group?: string;
  // Which parts of the business a report's data covers, shown as labels on its
  // Reports card (Steven, 2026-11-03; checked against each report's data):
  // lp-stores = the Lolli & Pops stores; lp-online = the L&P Shopify store
  // (DTC-LP); hb-store = the Hammond's factory store (CounterPoint 5735);
  // hb-online = the Hammond's Shopify store (DTC-HM); hb-wholesale = Hammond's
  // Sage warehouses and wholesale; warehouse = CounterPoint's warehouse
  // location (W-25000). Reports department only.
  covers?: Coverage[];
};

export type Coverage = 'lp-stores' | 'lp-online' | 'hb-store' | 'hb-online' | 'hb-wholesale' | 'warehouse';

export type Department = {
  id: string;
  name: string;
  short: string;
  blurb: string;
  tools: Tool[];
  // Optional capability string. When set, the sidebar hides the whole
  // department (and its tools) from any user whose permissions do not
  // include it. Absent = visible to all (current behavior unchanged).
  capability?: string;
  // Optional landing page. When set, the sidebar shows this department as ONE
  // link to this route instead of a collapsible list of its tools, and the
  // landing page lists the tools itself. Only Reports uses this today (AWS
  // cost plan Phase 3, Task 13, 2026-11-02): every report becomes a card on
  // /reports so the menu does not grow as QuickSight reports move over. The
  // Access console still lists each tool as its own checkbox row.
  menuHref?: string;
};

export const COCKPIT_DEPARTMENTS: Department[] = [
  {
    id: 'commerce',
    name: 'Commerce',
    short: 'Commerce',
    blurb: 'Orders, inventory, products, partners, reports.',
    tools: [
      { id: 'orders', name: 'Orders', desc: 'All sales orders across every channel.', status: 'progress', owner: 'Steven Pinto', channel: 'All channels', current: true, helpKbPageId: '61636610', accessSlug: 'commerce.orders' },
      { id: 'products', name: 'Products', desc: 'Sage catalog across both brands, with per-warehouse on-hand.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'commerce.products', helpKbPageId: '113016956' },
      { id: 'inventory', name: 'Inventory', desc: 'On-hand, committed, and available by item and warehouse.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'commerce.inventory', helpKbPageId: '113246319' },
      { id: 'partners', name: 'Partners', desc: 'Retailers, vendors, brokers, 3PLs.', status: 'planned', owner: 'Steven Pinto', launched: false },
      { id: 'reports', name: 'Reports', desc: 'Volume, fulfillment time, top retailers, backorder rate.', status: 'planned', owner: 'Steven Pinto', launched: false },
    ],
  },
  {
    id: 'crm',
    name: 'CRM',
    short: 'CRM',
    blurb: 'Wholesale accounts, deal pipeline, and the weekly scorecard.',
    // No department-level gate: each tool is independently grantable on its own
    // accessSlug (binary access-grant model, spec 00_access-binary-grants.md).
    // A crm:read department gate would hide the whole section for anyone
    // granted only one of the three tools below, since that literal capability
    // is never granted anymore. Removed 2026-08-09.
    tools: [
      { id: 'accounts', name: 'Accounts', desc: 'Wholesale accounts with open orders and weighted pipeline.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'crm.accounts' },
      { id: 'opportunities', name: 'Pipeline', desc: 'Open opportunities on a stage board, plus create and close.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'crm.pipeline' },
      { id: 'scorecard', name: 'Wholesale Scorecard', desc: 'Weighted pipeline, won this quarter, by-rep, and needs-attention.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'crm.scorecard' },
    ],
  },
  {
    id: 'it',
    name: 'IT',
    short: 'IT',
    blurb: 'Projects, network, support.',
    tools: [
      { id: 'projects', name: 'Projects', desc: 'In-Cockpit project and task tracker for IT specs.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.projects' },
      { id: 'netmon', name: 'Network monitor', desc: 'Per-store WAN latency, loss, and LAN device telemetry.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.netmon', helpKbPageId: '148013057' },
      { id: 'cockpit-ops', name: 'Cockpit Ops', desc: 'Live view of every Cockpit scheduled task and Lambda.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.cockpit-ops' },
      { id: 'agents', name: 'Agents', desc: 'Code-agent and helpdesk-agent dashboards. Run history and per-agent spend.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.agents' },
      { id: 'counter-validation', name: 'Counter Validation', desc: 'Cherry Creek 1173 people-counter validation: traffic counts with entrance-camera snapshots, side by side.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.counter-validation' },
      // Telemetry runs canned CloudWatch Logs Insights queries server-side, so
      // answering "who used what" no longer means opening the AWS console.
      { id: 'telemetry', name: 'Telemetry', desc: 'Traffic, errors, slow routes, and per-user activity across Cockpit, Sweet Spot, and Connect, read live from the logs.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.telemetry' },
      // Writes the What's new entries both apps show behind the avatar menu.
      // The READER page (/changelog) is deliberately not a catalog tool, the
      // same way /settings is not: it is open to every signed-in user.
      { id: 'changelog', name: 'Changelog', desc: "Write the What's new notes users see in Cockpit and The Sweet Spot.", status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.changelog' },
      // How much of what we built is actually being used, per tool against
      // the people or stores who hold it. Rocks-and-scorecard view of adoption.
      { id: 'adoption', name: 'Adoption', desc: 'How much of what we built is actually being used: per-tool usage against the people who hold each tool.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.adoption' },
      // Every notification Cockpit sent, with the channel and whether it arrived.
      // The per-person switches live in Settings > Notifications (not a tool).
      { id: 'notification-log', name: 'Notification log', desc: 'Every notification Cockpit sent: who, which channel, and whether it arrived.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.notifications', helpKbPageId: '150011909' },
      // Launch page for the api's Swagger UI at ${API_BASE}/docs, which needs a
      // signed-in session (api #712). Listed here so IT finds it in the sidebar.
      { id: 'api-docs', name: 'API docs', desc: 'Interactive documentation for every API endpoint behind Cockpit and The Sweet Spot.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'it.api-docs' },
    ],
  },
  {
    id: 'connect',
    name: 'Connect',
    short: 'Connect',
    blurb: 'Wholesale and broker partner portal administration: partners, pricing, agreements, adoption.',
    // No department-level gate: each tool is independently grantable on its own
    // accessSlug. The old connect:admin department gate is removed 2026-08-09
    // (binary access-grant model, spec 00_access-binary-grants.md) — that
    // literal capability is never granted anymore, so keeping the gate would
    // have hidden the whole section for anyone granted only one Connect tool.
    tools: [
      { id: 'connect-partners', name: 'Partners', desc: 'Wholesaler and broker organizations, their Sage mappings, users, and agreements.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'connect.partners' },
      { id: 'connect-pricing', name: 'Pricing', desc: 'Partner price list across all buckets, with file import and per-item history.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'connect.pricing' },
      { id: 'connect-agreements', name: 'Agreements', desc: 'Partner agreements across every org, with status and DocuSign envelope tracking.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'connect.agreements' },
      { id: 'connect-adoption', name: 'Adoption', desc: 'Portal adoption per partner: users, logins, and orders placed.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'connect.adoption' },
    ],
  },
  {
    id: 'stores',
    name: 'Stores',
    short: 'Stores',
    blurb: 'Store Operating System: the daily routine, the store-to-HQ thread, HQ broadcasts, the question queue, and the daily compliance scorecard.',
    // No department-level gate: the two audiences hold different capabilities
    // (stores:store for store managers, stores:hq for HQ). The tools below are
    // gated individually, and the sidebar hides the whole section when a user
    // holds neither (no launched+permitted tools). All three capabilities
    // (stores:store / stores:hq / stores:admin) are seeded in migration 0073.
    // Sidebar Option A redesign (Steven, approved 2026-08-08): the Stores
    // tools render as four labeled subgroups instead of one flat list, in
    // this exact order. See `Tool.group` below and `ui/src/shell/Sidebar.tsx`.
    tools: [
      // --- Daily ops ---
      // Sweet Spot: the NEW console row (spec 00_access-binary-grants.md,
      // 2026-08-09). Represents the store-facing Sweet Spot app as ONE
      // grantable unit: store accounts get it automatically (via store
      // provisioning), corporate users get it as an explicit grant plus a
      // store picker (sweetspot repo, out of scope here). Not a real Cockpit
      // route (launched: false, no page renders it), but it IS the canonical
      // name for the `stores.sweetspot` accessSlug, which Today / Store thread
      // / Inventory Count below also carry — holding it is holding the whole
      // store app, so it must sort first in this array: buildGrantableDepts
      // (accessScopes.ts) dedupes tools that share an accessSlug and uses the
      // FIRST one it finds for the console row's name.
      { id: 'stores-sweetspot', name: 'Sweet Spot', desc: 'The Sweet Spot store app. Store accounts get this automatically; corporate users get a store picker.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.sweetspot', launched: false, group: 'Daily ops' },
      { id: 'stores-today', name: 'Today', desc: 'The daily routine for your store: opening, midday, and closing check-ins with traffic, transactions, checklist, notes, and photos.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.sweetspot', group: 'Daily ops', helpKbPageId: '113246361' },
      // Retired: the store<->HQ thread folded into the global chat bubble; the
      // /stores/thread route now redirects. Kept in the catalog (so old bookmarks
      // redirect and Access-console parity holds) but hidden from the sidebar.
      { id: 'stores-thread', name: 'Store thread', desc: 'Your store\'s two-way conversation with HQ: messages, HQ broadcasts, and the questions you ask.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.sweetspot', launched: false, group: 'Daily ops' },
      { id: 'stores-hq', name: 'Daily scorecard', desc: 'HQ compliance view: which stores reported opening, midday, and closing today, with photos, traffic, and open questions.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.scorecard', group: 'Daily ops', helpKbPageId: '113377390' },
      { id: 'stores-hq-questions', name: 'Questions', desc: 'HQ queue of store questions, most urgent and oldest first, with an inline answer composer.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.questions', group: 'Daily ops', helpKbPageId: '113016994' },
      // Merch Pulse: HQ queue of store merchandising signals (too much, send
      // more, requested, problem, other) flagged against a SKU. Deliberately
      // its own queue, not a column on the Allocation grid (Steven rejected
      // that: the grid was trimmed for browser performance and adding a signal
      // per store per SKU there would slow it down again).
      { id: 'stores-hq-pulse', name: 'Merch Pulse', desc: 'HQ queue of store merchandising signals flagged against a SKU: too much, send more, requested, a problem, or other.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.pulse', group: 'Daily ops', helpKbPageId: '114130960' },
      { id: 'stores-hq-broadcast', name: 'Broadcast', desc: 'Send one message to all stores, the Pilot 16, or a custom set, with a history of past broadcasts.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.broadcast', group: 'Daily ops', helpKbPageId: '113377436' },
      { id: 'stores-hq-photos', name: 'Store photos', desc: 'Browse and review the merchandising photos stores attach to their daily Today check-ins, grouped by store and date.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.photos', group: 'Daily ops', helpKbPageId: '113213562' },
      // Chat is gated only on the core:read session baseline (like the HR
      // directory tools): channel membership is enforced server-side, so the
      // channels list only ever returns what the caller belongs to. An
      // Everyone tool (capability, not accessSlug) — spec 00_access-binary-
      // grants.md keeps Chat ungated-plus-membership.
      // Retired as a standalone page: chat is now the global bubble on every
      // page; the /stores/chat route redirects. Hidden from the sidebar.
      { id: 'stores-chat', name: 'Chat', desc: 'Team chat channels and Ask Amp: the same chat platform as the Sweet Spot store site, in a desktop window.', status: 'progress', owner: 'Steven Pinto', capability: 'core:read', launched: false, group: 'Daily ops' },

      // --- Inventory ---
      { id: 'stores-inventory', name: 'Inventory Count', desc: 'Count shelf stock and receive arriving orders by scanning barcodes. Counts are checked against CounterPoint on-hand and the purchase order.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.sweetspot', group: 'Inventory', helpKbPageId: '113344538' },
      // Cockpit only (not in The Sweet Spot). Generates the fleet-wide
      // physical-count file for CounterPoint from submitted shelf counts. Its
      // own independently-grantable accessSlug, so Steven can grant it
      // independent of the rest of the Stores admin surface, matching how Item
      // Sales gets its own accessSlug (Steven, 2026-08-07).
      { id: 'inventory-imports', name: 'Inventory Imports', desc: 'Generate the CounterPoint physical-count file from submitted shelf counts, and track which files have been imported.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.imports', group: 'Inventory', helpKbPageId: '113213448' },
      // Allocation keeps the /retail/allocation route. It moved from the
      // Performance subgroup to Inventory when the reports left Stores
      // (Steven, 2026-11-02): it is a working tool, not a report. Its own
      // independently-grantable accessSlug.
      { id: 'allocation', name: 'Allocation', desc: 'Weekly warehouse-to-store allocation runs, by store and item.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.allocation', group: 'Inventory', helpKbPageId: '113213590' },

      // --- Setup ---
      { id: 'stores-admin-users', name: 'Store users', desc: 'Assign accounts to stores. One form maps the email to a store with a role and auto-grants Stores access.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.users', group: 'Setup', helpKbPageId: '113377535' },
      // The tablet register. A registered tablet holds its own long-lived device
      // session, so store staff never sign in and never need the shared store
      // Google password. Its own accessSlug, distinct from Store users.
      { id: 'stores-admin-tablets', name: 'Store tablets', desc: 'Register the back-of-house tablets that run The Sweet Spot as their main screen, see when each one last checked in, and revoke a lost tablet.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.tablets', group: 'Setup', helpKbPageId: '113017090' },
      // The daily-routine checklist definition (opening/midday/closing items
      // store managers check off in Today). Its OWN accessSlug, deliberately not stores.users: store ops need to
      // edit checklist wording without also gaining the ability to assign
      // accounts to stores. The api's three checklist routes demand the same
      // slug (tools/stores/src/routes.ts).
      { id: 'stores-admin-checklist', name: 'Store checklist', desc: 'Edit the opening, midday, and closing checklist items store managers check off in Today.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.checklist', group: 'Setup', helpKbPageId: '114491434' },
      // Store content editor (HQ). Its own accessSlug, distinct from the rest
      // of Stores, so store-only accounts never see it.
      { id: 'stores-content', name: 'Store content', desc: 'Author, curate, and publish the pages the Sweet Spot store site shows to store managers: promotions, training, marketing, and store submissions.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.content', group: 'Setup', helpKbPageId: '113246484' },
      // One page per store for Store Ops and IT: the same Store Info and Store
      // Hours boxes the Sweet Spot My Store page shows (from the shared
      // @ampersandbrands/ui cards), plus the store's network carrier and
      // connection type, an append-only note log, and its photo and document
      // folders. Its OWN accessSlug: it exposes every store's details to
      // whoever holds it, so it is granted apart from the rest of Stores.
      { id: 'stores-info', name: 'Store Info', desc: 'One page per store: address, hours, manager, network, notes, and the store photo and document folders.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.info', group: 'Setup', helpKbPageId: '159154178' },
      { id: 'stores-paperwork', name: 'Paperwork', desc: 'Which stores have sent their CINTAS and Pest Control invoices and licenses this month, with links into the Paperwork folders of each store.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.paperwork', group: 'Setup' },
    ],
  },
  {
    // Reports (AWS cost plan Phase 3, Task 13, Steven approved 2026-11-02).
    // ONE sidebar link (menuHref) opening a card page grouped by topic, so the
    // menu stays the same size as QuickSight reports move into Cockpit. Each
    // report keeps its own route and its own accessSlug, so existing grants
    // and bookmarks are unchanged: the six below moved here from Stores and
    // still carry their stores.* slugs (the api routes demand those exact
    // strings). `group` is the topic heading on the Reports page, in the order
    // REPORT_TOPICS lists (ui/src/pages/reports/Reports.tsx). New reports land
    // here as cards, never as new sidebar links.
    id: 'reports',
    name: 'Reports',
    short: 'Reports',
    blurb: 'Every Cockpit report on one page, grouped by topic, with search and stars.',
    menuHref: '/reports',
    tools: [
      // --- Sales ---
      // OMNI Performance keeps the /retail/performance route. A store manager
      // holding stores.sweetspot reaches an own-store view (scope enforced
      // server-side).
      { id: 'performance', name: 'OMNI Performance', desc: 'Daily fleet report by region and store: sales, transactions, traffic, units, and DPT / UPT, with same-calendar-date and same-weekday last-year comparisons.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.performance', covers: ['lp-stores', 'lp-online', 'hb-store', 'hb-online'], group: 'Sales', helpKbPageId: '113377471' },
      // Revenue Leakage keeps the /retail/leakage route. Its own accessSlug; a
      // store manager holding stores.sweetspot reaches an own-store view.
      { id: 'leakage', name: 'Revenue Leakage', desc: 'Categories and SKUs ranked by dollar decline in sales versus the same trailing window last year, biggest losses first.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.leakage', covers: ['lp-stores', 'hb-store'], group: 'Sales', helpKbPageId: '113377499' },
      // Item Sales keeps the /retail/item-sales route. Holding it confers the
      // FLEET view: store_no is a filter, never a scope boundary.
      { id: 'item-sales', name: 'Item Sales', desc: 'What sold, at which store, by SKU, for one day: units, net sales, cost, and tickets across every store, including the Hammond\'s factory store.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.itemsales', covers: ['lp-stores', 'hb-store'], group: 'Sales', helpKbPageId: '113246428' },

      // --- Inventory ---
      { id: 'stores-hq-variance', name: 'Inventory variance', desc: 'HQ report over submitted store counts: counted vs expected with variance in units and dollars, and short-shipment flags on receiving.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.variance', covers: ['lp-stores', 'hb-store'], group: 'Inventory', helpKbPageId: '113017062' },

      // --- Store Analytics ---
      // Fleet Map keeps the /retail/fleet-map route; moved from Stores to
      // Reports 2026-11-02 (Steven). An Everyone tool (capability core:read,
      // not accessSlug), so every HQ employee sees this card and therefore the
      // Reports link, while store Google accounts (explicit grants only) never
      // do. Shows in the Access console as a read-only Everyone row.
      { id: 'fleet-map', name: 'Fleet Map', desc: 'Weekly scorecard map for every L&P store and the Hammond\'s factory store. Severity heat-map, Pilot 16 filter, and per-store sales detail.', status: 'progress', owner: 'Steven Pinto', capability: 'core:read', helpKbPageId: '68976652', covers: ['lp-stores', 'hb-store'], group: 'Store Analytics' },
      { id: 'stores-door-times', name: 'Door times', desc: 'When the first person came in and the last person left each day, at any hour, beside posted hours and first and last sale, with late opens and early closes flagged.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.doortimes', covers: ['lp-stores', 'hb-store'], group: 'Store Analytics', helpKbPageId: '154107906' },
      // Employee purchases (Steven, 2026-10-29): every line rung with the
      // CounterPoint EMPLOYEE discount. The api's two routes demand
      // stores.employeepurchases (tools/stores/src/routes.ts).
      { id: 'stores-employee-purchases', name: 'Employee purchases', desc: 'Every item rung with the employee discount: who rang it, the item, price, discounts, and payment, with returns, price overrides, and extra discounts flagged.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'stores.employeepurchases', covers: ['lp-stores', 'hb-store'], group: 'Store Analytics', helpKbPageId: '163840001' },
      // Unposted Activity (Steven, 2026-11-02): replaces the QuickSight Unposted
      // Activity Report and Unposted Inventory Analysis. The api's two routes
      // demand reports.unposted (tools/retail/src/unposted-routes.ts); a store
      // manager holding stores.sweetspot reaches an own-store view.
      { id: 'unposted-activity', name: 'Unposted Activity', desc: 'Register drawers opened before today and inventory, purchasing, and transfer documents CounterPoint has not posted yet, by store and type.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.unposted', covers: ['lp-stores', 'hb-store', 'warehouse'], group: 'Store Analytics', helpKbPageId: '165773313' },
      // Loyalty (Steven, 2026-11-02): replaces the QuickSight WTD Loyalty Dashboard,
      // Weekly Loyalty Performance Dashboard and the Loyalty sheet of OMNI Sales and
      // Loyalty. The api's two routes demand reports.loyalty
      // (tools/retail/src/loyalty-routes.ts); a store manager holding stores.sweetspot
      // reaches an own-store view.
      { id: 'loyalty', name: 'Loyalty', desc: 'How much of our sales went to loyalty members and how many new members we signed up, by store and day, for the week to date or any fiscal week.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.loyalty', covers: ['lp-stores', 'hb-store'], group: 'Loyalty' },
      // Store Analytics (Steven, 2026-11-02): replaces the QuickSight Returns Dashboard,
      // Discount Dashboard and Bulk Penetration Performance, as three sections of one
      // report. The api's two routes demand reports.storeanalytics
      // (tools/retail/src/store-analytics-routes.ts); a store manager holding
      // stores.sweetspot reaches an own-store view.
      { id: 'store-analytics', name: 'Store Analytics', desc: 'Returns, discounts, and bulk penetration by store, for any fiscal week or day, compared with the same days last year.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.storeanalytics', covers: ['lp-stores', 'hb-store'], group: 'Store Analytics' },
      // On Order (Steven, 2026-11-03): replaces the QuickSight Product On Order dashboard.
      // The api's two routes demand reports.onorder (tools/retail/src/on-order-routes.ts);
      // a store manager holding stores.sweetspot reaches an own-store view.
      { id: 'on-order', name: 'On Order', desc: 'Every line on an open purchase order and what has not arrived yet: quantity ordered and received, the cost still open, and the order, due, and cancel dates, by store and vendor.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.onorder', covers: ['lp-stores', 'hb-store', 'warehouse'], group: 'Inventory' },
      // Item File (Steven, 2026-11-03): the item file Tim feeds to a downstream system, replacing the QuickSight
      // dataset MapTbl_Products. Was Inventory Exports; the store stock half moved to Commerce > Inventory. The api's
      // two routes demand reports.itemfile (tools/retail/src/item-file-routes.ts); a store manager holding
      // stores.sweetspot may also read it (the item file is not store data).
      // Shortcut to Commerce > Inventory (Steven, 2026-11-03): store stock with
      // cost, value and Excel export, plus Hammond's warehouses. Same accessSlug
      // as the Commerce tool, so the Access console shows one row for both.
      { id: 'commerce-inventory', name: 'Inventory', desc: "On hand, cost and value by item for every Lolli & Pops store and Hammond's warehouse, live or for a past date, with an Excel download.", status: 'progress', owner: 'Steven Pinto', accessSlug: 'commerce.inventory', covers: ['lp-stores', 'hb-store', 'hb-wholesale'], group: 'Inventory', helpKbPageId: '113246319' },
      { id: 'item-file', name: 'Item File', desc: 'Every item with its status, department, vendor, price, last cost and UPC. Download the full 59-column item file as Excel.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.itemfile', covers: ['lp-stores', 'hb-store'], group: 'Inventory' },
      // Fleet Hourly (Steven, 2026-11-03): replaces the QuickSight Store Hourly Dashboard. The api's two
      // routes demand reports.fleethourly (tools/retail/src/fleet-hourly-routes.ts); a store manager holding
      // stores.sweetspot reaches an own-store view. The store drill-in reads GET /v1/stores/omni-hourly, which
      // accepts reports.fleethourly as well.
      { id: 'fleet-hourly', name: 'Fleet Hourly', desc: 'Every store hour by hour against the same weekday last year: sales, transactions, traffic, and conversion, with a fleet row, any past day, and an Excel download.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.fleethourly', covers: ['lp-stores', 'hb-store'], group: 'Sales' },
      // Finance reports (Steven, 2026-11-03): five head office reports that replace QuickSight dashboards. The api's
      // routes demand each slug (tools/retail/src/finance-routes.ts) with no store-manager fallback, so a store
      // manager holding stores.sweetspot sees none of them. The data covers physical stores only (Lolli & Pops and the
      // Hammond's factory store, 5735); no online store, wholesale or warehouse rows appear in any of the five.
      { id: 'cash-credit', name: 'Cash & Credit', desc: 'Cash and credit sales by store for any range of business days, by store or by store and day, with a store filter, a totals row, and an Excel download. Opens on the last complete fiscal week.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.cashcredit', covers: ['lp-stores', 'hb-store'], group: 'Finance' },
      { id: 'trailing-12', name: 'Trailing 12 Months', desc: 'Sales by store for each of the last twelve completed calendar months, with a total for every store and every month, and an Excel download.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.trailing12', covers: ['lp-stores', 'hb-store'], group: 'Finance' },
      { id: 'sales-tax', name: 'Sales Tax', desc: 'Taxable and non-taxable sales by store and tax authority for any range of business days, with tax charged, a totals row, and an Excel download laid out for filing. Opens on the last complete month.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.salestax', covers: ['lp-stores', 'hb-store'], group: 'Finance' },
      { id: 'gift-cards', name: 'Gift Cards', desc: 'Gift card purchases and redemptions by fiscal month, the balance still on cards, and balances by month sold and selling store, with an Excel download. The breakage factor and the 2020 cut-off date are pending Finance confirmation.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.giftcards', covers: ['lp-stores', 'hb-store'], group: 'Finance' },
      { id: 'gl-exports', name: 'GL Exports', desc: 'Download the Sales journal or the Inventory journal for a fiscal month as an Excel file, after checking its line count and debit and credit totals.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'reports.glexports', covers: ['lp-stores', 'hb-store'], group: 'Finance' },
    ],
  },
  {
    id: 'compliance',
    name: 'Compliance',
    short: 'Compliance',
    blurb: 'Certificates of Analysis, supplier documents, regulatory records.',
    tools: [
      {
        id: 'coas',
        name: 'CoAs',
        desc: 'Certificates of Analysis filed by PO. Review, correct, and tag supplier certificates side by side with the PDF.',
        status: 'progress',
        owner: 'Steven Pinto',
        helpKbPageId: '68943899',
        accessSlug: 'compliance.coas',
      },
    ],
  },
  {
    id: 'finance',
    name: 'Finance',
    short: 'Finance',
    blurb: 'Vendor invoices, documents, financial records.',
    // Department-level documents.read gate removed 2026-08-09 (binary
    // access-grant model): the single tool below carries its own accessSlug,
    // and that literal capability is never granted anymore.
    tools: [
      {
        id: 'invoices',
        name: 'Invoices',
        desc: 'Vendor invoices captured from email. Review the PDF beside the extracted vendor, dates, and totals; correct any field.',
        status: 'progress',
        owner: 'Steven Pinto',
        helpKbPageId: '83755009',
        accessSlug: 'finance.invoices',
      },
    ],
  },
  {
    id: 'comms',
    name: 'Comms',
    short: 'Comms',
    blurb: 'Phone reporting: call summary, callbacks, and voicemail.',
    // Department-level comms:read gate removed 2026-08-09 (binary
    // access-grant model): each tool below carries its own accessSlug, and
    // that literal capability is never granted anymore.
    tools: [
      {
        id: 'comms-dashboard',
        name: 'Dashboard',
        desc: 'Daily call summary, abandoned-call callbacks, and voicemail box status.',
        status: 'progress',
        owner: 'Steven Pinto',
        accessSlug: 'comms.dashboard',
        helpKbPageId: '93159425',
      },
      {
        id: 'comms-calllog',
        name: 'Call Log',
        desc: 'Every inbound call for a day: who they were trying to reach, outcome, wait time, with filters and Excel export.',
        status: 'progress',
        owner: 'Steven Pinto',
        accessSlug: 'comms.calllog',
        helpKbPageId: '93159425',
      },
    ],
  },
  {
    id: 'hr',
    name: 'HR',
    short: 'HR',
    blurb: 'Onboarding, people, time off.',
    tools: [
      // The retired 'org-chart' tool lives on only as a redirect to the
      // directory in App.tsx. The directory is ordered by reporting line, so it
      // already is the org chart, and a second tool rendering the same tree just
      // drifted from it.
      { id: 'directory', name: 'Directory', desc: 'Everyone in the company: corporate by reporting line, then store by store, with phone, extension and email throughout.', status: 'progress', owner: 'Helen Cobb', capability: 'core:read' },
      // Payroll send: the api route `POST /v1/core/employees/ingest` (the
      // Rippling employee list posted by Helen's Payroll Reports program)
      // requires the `hr.directorysend` slug. There is no page for it, so it
      // is unlaunched to stay out of the sidebar while the Access console
      // still renders a checkbox for it (buildGrantableDepts reads every
      // tool with an accessSlug, launched or not).
      { id: 'directory-send', name: 'Directory: payroll send', desc: 'Lets the Payroll Reports program post the Rippling employee list into the Cockpit directory. Granted only to the person who runs payroll.', status: 'progress', owner: 'Cyrus Xiong', accessSlug: 'hr.directorysend', launched: false },
      { id: 'onboarding', name: 'Onboarding', desc: 'New-hire checklist, equipment, access.', status: 'planned', owner: 'Helen Cobb', launched: false },
      { id: 'time-off', name: 'Time off', desc: 'Request, view balance, calendar.', status: 'planned', owner: 'Helen Cobb', launched: false },
      { id: 'benefits', name: 'Benefits', desc: 'Health, retirement, candy allowance.', status: 'planned', owner: 'Helen Cobb', launched: false },
    ],
  },
  {
    id: 'administration',
    // Display name shortened from 'Administration' so the widest sidebar
    // label stops setting the sidebar's width (Steven, 2026-08-08).
    name: 'Admin',
    short: 'Admin',
    blurb: 'Manage who can access each Cockpit tool.',
    // Department-level access.admin gate removed 2026-08-09 (binary
    // access-grant model): each tool below carries its own accessSlug, and
    // that literal capability is never granted anymore.
    tools: [
      { id: 'access', name: 'Access', desc: 'Grant and review user access to Cockpit tools.', status: 'progress', owner: 'Steven Pinto', launched: true, accessSlug: 'admin.access', helpKbPageId: '71401473' },
      // KB Feedback: a console-only entry, no sidebar presence and no route,
      // same pattern as the Sweet Spot row above (Tool.launched: false).
      // Gates the "Improve this article" box at the bottom of an open
      // article in the shared HelpPanel, in both Cockpit and The Sweet Spot.
      // admin.access itself stays excluded from the grantable console (see
      // accessScopes.ts, buildGrantableDepts) so nobody can bootstrap the
      // Access console from inside itself; that exclusion is specific to
      // admin.access and does not apply to this tool, so KB Feedback renders
      // as an ordinary checkbox row under Admin.
      { id: 'admin-kb-feedback', name: 'KB Feedback', desc: 'Lets a granted user submit "this article is missing X" instructions from the Help panel, tied to the exact article, for the docs owner to act on.', status: 'progress', owner: 'Steven Pinto', accessSlug: 'admin.kb-feedback', launched: false },
    ],
  },
];

// Returns true when a tool should appear in the sidebar and home page.
// Absent or true = visible. Explicitly false = hidden (but routes remain).
export function isLaunched(entity: { launched?: boolean }): boolean {
  return entity.launched !== false;
}

// Server-side allowlist for pin POSTs. Derived from COCKPIT_DEPARTMENTS so it
// cannot drift from the UI catalog. ReadonlySet for both ergonomics and intent.
export type ToolSlug = string;

export const VALID_TOOL_SLUGS: ReadonlySet<ToolSlug> = new Set<ToolSlug>(
  COCKPIT_DEPARTMENTS.flatMap((d) => d.tools.map((t) => t.id)),
);

export function isValidToolSlug(slug: string | undefined | null): slug is ToolSlug {
  if (typeof slug !== 'string') return false;
  if (slug.length === 0 || slug.length > 128) return false;
  return VALID_TOOL_SLUGS.has(slug);
}

// Helpers.
export function findDepartment(deptId: string): Department | undefined {
  return COCKPIT_DEPARTMENTS.find((d) => d.id === deptId);
}

export function findTool(
  deptId: string,
  toolId: string,
): { dept: Department; tool: Tool } | undefined {
  const dept = findDepartment(deptId);
  if (!dept) return undefined;
  const tool = dept.tools.find((t) => t.id === toolId);
  if (!tool) return undefined;
  return { dept, tool };
}

export function totals(): { live: number; progress: number; planned: number; total: number } {
  let live = 0;
  let progress = 0;
  let planned = 0;
  for (const d of COCKPIT_DEPARTMENTS) {
    for (const t of d.tools) {
      if (t.status === 'live') live += 1;
      else if (t.status === 'progress') progress += 1;
      else planned += 1;
    }
  }
  return { live, progress, planned, total: live + progress + planned };
}
